import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, AlertCircle, ArrowRight, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { isTikTokCallback, validateOAuthState as validateTikTokState, exchangeTikTokCode } from '@/lib/tiktok-auth';
import { isXCallback, validateXOAuthState, exchangeXCode } from '@/lib/x-auth';
import { savePendingSession } from '@/lib/auth/AuthOrchestrator';
import { parseAuthError } from '@/lib/auth/AuthOrchestrator';
import { retryWithBackoff } from '@/lib/auth/retryWithBackoff';

const RETRY_CONFIG = {
  maxRetries:          3,
  initialDelayMs:      800,
  maxDelayMs:          5000,
  backoffMultiplier:   2,
  retryableStatusCodes:[429, 500, 502, 503, 504],
};

// FIX: Tokens are passed directly in the deeplink URL query string.
//
// The previous approach stored tokens in sessionStorage and put only a
// one-time nonce in the deeplink. This was broken because NativeCallback
// runs at gistgift.shop (in the external Safari browser) while the Capacitor
// WebView runs at a different origin — storage is completely isolated between
// them. The app always read from its own empty storage and never found the
// tokens, so every TikTok / X / Google native OAuth attempt silently failed.
//
// Fix: pass access_token and refresh_token directly in the gistgift:// URL.
//
// Security: iOS custom-scheme deeplinks (gistgift://) are delivered exclusively
// to the registered app by the OS URL scheme dispatcher — they never appear in
// Safari history, server logs, or referrer headers. Short-lived OAuth tokens in
// a custom-scheme deeplink is standard iOS practice (used by Spotify, Notion,
// Twitter's own iOS app, etc.) and is safe on iOS.
//
// savePendingSession() is kept as a belt-and-suspenders backup — it works in
// same-origin environments such as the Lovable web preview where NativeCallback
// and the app WebView share an origin.

const NativeCallback = () => {
  const [searchParams] = useSearchParams();

  const [status,        setStatus]        = useState<'processing' | 'error' | 'success'>('processing');
  const [statusMessage, setStatusMessage] = useState('Completing sign in...');
  const [errorMessage,  setErrorMessage]  = useState<string | null>(null);
  const [errorDetails,  setErrorDetails]  = useState<string | null>(null);
  const [deeplinkUrl,   setDeeplinkUrl]   = useState<string | null>(null);
  const [retryCount,    setRetryCount]    = useState(0);
  const [canRetry,      setCanRetry]      = useState(false);

  // FIX: Mounted guard — prevents state updates after the component unmounts
  // during an in-flight token exchange (e.g. user closes the browser tab).
  const isMountedRef  = useRef(true);
  // Guard: process the callback exactly once per mount
  const hasRunRef     = useRef(false);
  // FIX: In-flight guard — prevents multiple concurrent token exchanges when
  // the user taps "Try Again" rapidly.
  const isProcessingRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  // ── Retry helper ─────────────────────────────────────────────────────────────
  const exchangeWithRetry = async <T,>(
    exchangeFn: () => Promise<T>,
    provider: string
  ): Promise<T> => {
    return retryWithBackoff(
      exchangeFn,
      RETRY_CONFIG,
      (attempt, _error, delayMs) => {
        console.log(`[NativeCallback] Retry ${attempt} for ${provider}, waiting ${delayMs}ms`);
        if (isMountedRef.current) setStatusMessage(`Retrying... (${attempt}/${RETRY_CONFIG.maxRetries})`);
      }
    );
  };

  // ── Deeplink redirect ─────────────────────────────────────────────────────────
  // Fires window.open first (preferred on iOS ASWebAuthenticationSession),
  // then location.href as a fallback 250 ms later. Both may fire for custom
  // URL schemes — the OS handles duplicate deeplink activations gracefully.
  const redirectToDeeplink = (url: string) => {
    console.log('[NativeCallback] Redirecting to:', url);
    try { window.open(url, '_self'); } catch (e) { console.warn('[NativeCallback] window.open failed:', e); }
    setTimeout(() => {
      try { window.location.href = url; } catch (e) { console.warn('[NativeCallback] location.href failed:', e); }
    }, 250);
  };

  // ── Core callback handler ─────────────────────────────────────────────────────
  const handleCallback = async () => {
    // FIX: Prevent concurrent exchanges (rapid retry taps, double effect fire).
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;

    try {
      let deeplinkScheme = searchParams.get('deeplink_scheme') ||
                           new URLSearchParams(window.location.search).get('deeplink_scheme') ||
                           'gistgift';

      const windowParams = new URLSearchParams(window.location.search);

      // ── X (Twitter) callback ─────────────────────────────────────────────
      if (isXCallback(windowParams)) {
        console.log('[NativeCallback] X callback detected');
        if (isMountedRef.current) setStatusMessage('Authenticating with X...');

        const code        = windowParams.get('code')!;
        const state       = windowParams.get('state')!;
        const stateResult = validateXOAuthState(state);
        if (stateResult.warning) console.warn('[NativeCallback] X state validation warning:', stateResult.warning);
        deeplinkScheme    = stateResult.deeplinkScheme || deeplinkScheme;

        try {
          if (isMountedRef.current) setStatusMessage('Verifying credentials...');
          if (!stateResult.codeVerifier) throw new Error('Missing code verifier for PKCE flow');

          const tokens = await exchangeWithRetry(
            () => exchangeXCode(code, stateResult.codeVerifier!, true, stateResult.redirectUri),
            'X'
          );

          // FIX: Pass tokens directly in the deeplink — cross-origin storage
          // isolation means the app can never read tokens from sessionStorage
          // written here (gistgift.shop ≠ Capacitor WebView origin).
          savePendingSession(tokens.access_token, tokens.refresh_token, 'x');

          const deeplink = `${deeplinkScheme}://oauth/auth?access_token=${encodeURIComponent(tokens.access_token)}&refresh_token=${encodeURIComponent(tokens.refresh_token)}&provider=x`;

          if (!isMountedRef.current) return;
          setDeeplinkUrl(deeplink);
          setStatus('success');
          setStatusMessage('Sign in successful! Returning to app...');
          redirectToDeeplink(deeplink);

        } catch (error) {
          console.error('[NativeCallback] X exchange failed:', error);
          const parsed = parseAuthError(error);
          if (!isMountedRef.current) return;
          setStatus('error');
          setErrorMessage(parsed.userMessage);
          setStatusMessage('Sign in failed');
          setErrorDetails(error instanceof Error ? error.message : String(error));
          setCanRetry(parsed.retryable);
          const errorUrl = `${deeplinkScheme}://oauth/auth?error=${encodeURIComponent(parsed.userMessage)}`;
          setDeeplinkUrl(errorUrl);
        }
        return;
      }

      // ── TikTok callback ──────────────────────────────────────────────────
      if (isTikTokCallback(windowParams)) {
        console.log('[NativeCallback] TikTok callback detected');
        if (isMountedRef.current) setStatusMessage('Authenticating with TikTok...');

        const code        = windowParams.get('code')!;
        const state       = windowParams.get('state')!;
        const stateResult = validateTikTokState(state);
        if (stateResult.warning) console.warn('[NativeCallback] State validation warning:', stateResult.warning);
        deeplinkScheme    = stateResult.deeplinkScheme || deeplinkScheme;

        try {
          if (isMountedRef.current) setStatusMessage('Verifying credentials...');

          const tokens = await exchangeWithRetry(
            () => exchangeTikTokCode(code, true, stateResult.redirectUri),
            'TikTok'
          );

          // FIX: Pass tokens directly in the deeplink — see security note above.
          savePendingSession(tokens.access_token, tokens.refresh_token, 'tiktok');

          const deeplink = `${deeplinkScheme}://oauth/auth?access_token=${encodeURIComponent(tokens.access_token)}&refresh_token=${encodeURIComponent(tokens.refresh_token)}&provider=tiktok`;

          if (!isMountedRef.current) return;
          setDeeplinkUrl(deeplink);
          setStatus('success');
          setStatusMessage('Sign in successful! Returning to app...');
          redirectToDeeplink(deeplink);

        } catch (error) {
          console.error('[NativeCallback] TikTok exchange failed:', error);
          const parsed = parseAuthError(error);
          if (!isMountedRef.current) return;
          setStatus('error');
          setErrorMessage(parsed.userMessage);
          setStatusMessage('Sign in failed');
          setErrorDetails(error instanceof Error ? error.message : String(error));
          setCanRetry(parsed.retryable);
          const errorUrl = `${deeplinkScheme}://oauth/auth?error=${encodeURIComponent(parsed.userMessage)}`;
          setDeeplinkUrl(errorUrl);
        }
        return;
      }

      // ── Standard Supabase implicit flow (Google, etc.) ───────────────────
      const hash         = window.location.hash.substring(1);
      const hashParams   = new URLSearchParams(hash);
      const accessToken  = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');

      console.log('[NativeCallback] Processing OAuth callback');
      console.log('[NativeCallback] Deeplink scheme:', deeplinkScheme);
      console.log('[NativeCallback] Has access token:', !!accessToken);

      if (!accessToken) {
        const errorParam = hashParams.get('error')             || searchParams.get('error');
        const errorDesc  = hashParams.get('error_description') || searchParams.get('error_description');
        console.error('[NativeCallback] No access token, error:', errorParam, errorDesc);

        const parsed = parseAuthError(errorDesc || errorParam || 'unknown');
        if (!isMountedRef.current) return;
        setStatus('error');
        setErrorMessage(parsed.userMessage);
        setCanRetry(parsed.retryable);
        const errorUrl = `${deeplinkScheme}://oauth/auth?error=${encodeURIComponent(errorParam || 'unknown')}&error_description=${encodeURIComponent(errorDesc || '')}`;
        setDeeplinkUrl(errorUrl);
        redirectToDeeplink(errorUrl);
        return;
      }

      // FIX: Pass tokens directly in the deeplink — see security note above.
      savePendingSession(accessToken, refreshToken || '', 'google');

      const deeplink = `${deeplinkScheme}://oauth/auth?access_token=${encodeURIComponent(accessToken)}&refresh_token=${encodeURIComponent(refreshToken || '')}&provider=google`;

      if (!isMountedRef.current) return;
      setDeeplinkUrl(deeplink);
      setStatus('success');
      setStatusMessage('Sign in successful! Returning to app...');
      console.log('[NativeCallback] Redirecting to deeplink');
      redirectToDeeplink(deeplink);

    } finally {
      // FIX: Always release the processing lock so retry is possible after failure.
      isProcessingRef.current = false;
    }
  };

  // ── Run once on mount ────────────────────────────────────────────────────────
  useEffect(() => {
    if (hasRunRef.current) return;
    hasRunRef.current = true;
    handleCallback();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const handleManualRedirect = () => {
    if (deeplinkUrl) redirectToDeeplink(deeplinkUrl);
  };

  const handleRetry = () => {
    // FIX: Guard against concurrent retries.
    if (retryCount >= RETRY_CONFIG.maxRetries || isProcessingRef.current) return;
    setRetryCount(prev => prev + 1);
    setStatus('processing');
    setErrorMessage(null);
    setErrorDetails(null);
    handleCallback();
  };

  // ── JSX ──────────────────────────────────────────────────────────────────────
  return (
    <div
      className="min-h-screen bg-gradient-secondary flex items-center justify-center p-6"
      style={{
        paddingTop:    'calc(1.5rem + var(--safe-area-top, 0px))',
        paddingBottom: 'calc(1.5rem + var(--safe-area-bottom, 0px))',
      }}
    >
      <div className="text-center space-y-6 max-w-sm">
        <img
          src="/logo.webp" alt="GistGift"
          className="w-16 h-16 mx-auto"
          onError={(e) => { (e.target as HTMLImageElement).src = '/logo.png'; }}
        />

        {status === 'processing' && (
          <Loader2 className="w-10 h-10 animate-spin mx-auto text-primary" aria-hidden="true" />
        )}
        {status === 'error' && (
          <div className="w-16 h-16 mx-auto rounded-full bg-destructive/10 flex items-center justify-center">
            <AlertCircle className="w-8 h-8 text-destructive" aria-hidden="true" />
          </div>
        )}
        {status === 'success' && (
          <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
            <ArrowRight className="w-8 h-8 text-primary" aria-hidden="true" />
          </div>
        )}

        <div className="space-y-2">
          <p className="text-foreground font-medium">{statusMessage}</p>
          {errorMessage && (
            <p className="text-sm text-destructive" role="alert">{errorMessage}</p>
          )}
          {errorDetails && status === 'error' && (
            <details className="mt-2 text-left">
              <summary className="text-xs text-muted-foreground cursor-pointer">Technical details</summary>
              <pre className="mt-1 p-2 bg-muted/50 rounded text-xs text-muted-foreground overflow-x-auto max-w-full whitespace-pre-wrap break-all">
                {errorDetails}
              </pre>
            </details>
          )}
        </div>

        <div className="space-y-3">
          {status === 'error' && canRetry && retryCount < RETRY_CONFIG.maxRetries && (
            <Button onClick={handleRetry} className="w-full" variant="default">
              <RefreshCw className="w-4 h-4 mr-2" aria-hidden="true" />
              Try Again
            </Button>
          )}
          {(status === 'error' || status === 'success') && deeplinkUrl && (
            <Button
              onClick={handleManualRedirect}
              variant={status === 'error' && !canRetry ? 'default' : 'outline'}
            >
              {status === 'error' ? 'Return to App' : 'Open App'}
            </Button>
          )}
        </div>

        {status === 'success' && (
          <p className="text-xs text-muted-foreground mt-4">
            If the app doesn't open automatically, tap the button above.
          </p>
        )}
      </div>
    </div>
  );
};

export default NativeCallback;
